import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';

// One Blender-authored static asset is shared by the city and its portal views.
// The authoritative gate list controls each bay; the model never creates links.
export function createCityArcadeLibrary(loadAsset=async()=>{
  const controller=new AbortController(),timeout=setTimeout(()=>controller.abort(),15000);
  try {
    const response=await fetch(new URL('./ssx-city-arcade.glb?v=74ebbb241b558a76',import.meta.url),{signal:controller.signal});
    if(!response.ok)throw Error('City arcade asset unavailable');
    const max=4*1024*1024;
    if(Number(response.headers.get('content-length'))>max)throw Error('City arcade exceeds asset budget');
    const reader=response.body.getReader(),parts=[];let size=0;
    for(;;){const {done,value}=await reader.read();if(done)break;size+=value.byteLength;if(size>max){await reader.cancel();throw Error('City arcade exceeds asset budget');}parts.push(value);}
    const bytes=new Uint8Array(size);let offset=0;for(const part of parts){bytes.set(part,offset);offset+=part.byteLength;}
    return (await new GLTFLoader().parseAsync(bytes.buffer,'')).scene;
  } finally {clearTimeout(timeout);}
}) {
  let entry=null;
  const dispose=scene=>{
    const resources=new Set();scene.traverse(o=>{if(o.geometry)resources.add(o.geometry);for(const m of o.material?(Array.isArray(o.material)?o.material:[o.material]):[]){resources.add(m);for(const v of Object.values(m))if(v?.isTexture)resources.add(v);}});
    for(const resource of resources)resource.dispose();
  };
  const releaseUnused=e=>{if(e.refs===0&&e.scene){dispose(e.scene);e.scene=null;if(entry===e)entry=null;}};
  return {retain(){
    if(!entry){const e={refs:0,scene:null};entry=e;e.ready=Promise.resolve().then(loadAsset).then(scene=>{
      if(!scene?.isObject3D)throw Error('Invalid city arcade scene');
      scene.traverse(o=>{if(o.isMesh){o.castShadow=true;o.receiveShadow=true;}for(const m of o.material?(Array.isArray(o.material)?o.material:[o.material]):[])m.toneMapped=true;});
      e.scene=scene;releaseUnused(e);return scene;
    }).catch(error=>{if(entry===e)entry=null;throw error;});}
    const e=entry;e.refs++;let released=false;
    return {ready:e.ready.then(scene=>released?null:scene.clone(true)),release(){if(released)return;released=true;e.refs--;releaseUnused(e);}};
  }};
}
const library=createCityArcadeLibrary();
export function createCityArcadeView(parent,{courseIds=[],layout=null,retain=()=>library.retain()}={}) {
  let active=new Set(courseIds),districtLayout=layout,handle=null,root=null,disposed=false,generation=0;
  const apply=()=>{if(root)for(const group of root.children)group.visible=group.name==='shared'||active.has(group.name)||(group.name==='victory'&&districtLayout?.victoryMoved===true)||(group.name==='destinations'&&districtLayout?.destinationSigns===true);};
  const update=(ids,nextLayout=districtLayout)=>{
    active=new Set(ids);districtLayout=nextLayout;
    if(disposed)return;
    if(!active.size&&!districtLayout){generation++;root?.removeFromParent();root=null;handle?.release();handle=null;return;}
    if(root){apply();return;}
    if(handle)return;
    const token=++generation;handle=retain();
    handle.ready.then(scene=>{if(disposed||generation!==token||!scene)return;root=scene;root.name='SSX city arcade · authored Blender asset';apply();parent.add(root);}).catch(()=>{if(generation===token){handle?.release();handle=null;}});
  };
  update(courseIds);
  return {update,dispose(){if(disposed)return;disposed=true;generation++;root?.removeFromParent();root=null;handle?.release();handle=null;}};
}
